import { supabase } from './supabase';
import { SupabaseError, withErrorHandling } from './errorHandling';

/**
 * Admin Profile Service
 * Handles admin-specific profile operations with proper error handling and RLS enforcement
 */

class AdminProfileService {
  /**
   * Update admin profile with proper RLS enforcement
   * Only admins can update their own profile
   */
  async updateAdminProfile(adminId, profileData) {
    return withErrorHandling(
      async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .update({
            full_name: profileData.full_name,
            email: profileData.email,
            phone: profileData.phone || null,
            location: profileData.location || null,
            bio: profileData.bio || null,
            security_settings: profileData.security_settings || {},
            security_questions: profileData.security_questions || [],
            updated_at: new Date().toISOString()
          })
          .eq('id', adminId)
          .eq('role', 'admin') // Ensure only admins can update themselves
          .select('*')
          .single();

        if (error) {
          throw new SupabaseError(error.message, error, { 
            table: 'user_profiles', 
            action: 'update_admin_profile' 
          });
        }

        return { success: true, data };
      },
      { table: 'user_profiles', action: 'update_admin_profile' }
    );
  }

  /**
   * Get admin profile by ID
   * Uses RLS to ensure only authorized access
   */
  async getAdminProfile(adminId) {
    return withErrorHandling(
      async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .select(`
            id,
            full_name,
            email,
            phone,
            location,
            bio,
            role,
            security_settings,
            security_questions,
            last_login,
            is_active,
            created_at,
            updated_at
          `)
          .eq('id', adminId)
          .eq('role', 'admin')
          .single();

        if (error) {
          throw new SupabaseError(error.message, error, { 
            table: 'user_profiles', 
            action: 'get_admin_profile' 
          });
        }

        return { success: true, data };
      },
      { table: 'user_profiles', action: 'get_admin_profile' }
    );
  }

  /**
   * Update admin security settings
   */
  async updateSecuritySettings(adminId, securitySettings) {
    return withErrorHandling(
      async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .update({
            security_settings: securitySettings,
            updated_at: new Date().toISOString()
          })
          .eq('id', adminId)
          .eq('role', 'admin')
          .select('security_settings')
          .single();

        if (error) {
          throw new SupabaseError(error.message, error, { 
            table: 'user_profiles', 
            action: 'update_security_settings' 
          });
        }

        return { success: true, data };
      },
      { table: 'user_profiles', action: 'update_security_settings' }
    );
  }

  /**
   * Update admin security questions
   */
  async updateSecurityQuestions(adminId, securityQuestions) {
    return withErrorHandling(
      async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .update({
            security_questions: securityQuestions,
            updated_at: new Date().toISOString()
          })
          .eq('id', adminId)
          .eq('role', 'admin')
          .select('security_questions')
          .single();

        if (error) {
          throw new SupabaseError(error.message, error, { 
            table: 'user_profiles', 
            action: 'update_security_questions' 
          });
        }

        return { success: true, data };
      },
      { table: 'user_profiles', action: 'update_security_questions' }
    );
  }

  /**
   * Get admin login history
   */
  async getAdminLoginHistory(adminId, limit = 10) {
    return withErrorHandling(
      async () => {
        const { data, error } = await supabase
          .from('login_sessions')
          .select('*')
          .eq('user_id', adminId)
          .order('login_time', { ascending: false })
          .limit(limit);

        if (error) {
          throw new SupabaseError(error.message, error, { 
            table: 'login_sessions', 
            action: 'get_login_history' 
          });
        }

        return { success: true, data };
      },
      { table: 'login_sessions', action: 'get_login_history' }
    );
  }

  /**
   * Log admin actions (audit trail)
   */
  async logAdminAction(adminId, actionType, targetUserId = null, targetJobId = null, targetReportId = null, reason = null, notes = null) {
    return withErrorHandling(
      async () => {
        const { data, error } = await supabase
          .from('admin_actions')
          .insert({
            admin_user_id: adminId,
            action_type: actionType,
            target_user_id: targetUserId,
            target_job_id: targetJobId,
            target_report_id: targetReportId,
            reason,
            notes,
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) {
          throw new SupabaseError(error.message, error, { 
            table: 'admin_actions', 
            action: 'log_admin_action' 
          });
        }

        return { success: true, data };
      },
      { table: 'admin_actions', action: 'log_admin_action' }
    );
  }
}

export const adminProfileService = new AdminProfileService();
